/**
 * useOrderFilters
 *
 * URL persistence and filter state management for the Orders page.
 * Handles filter state, URL synchronization, and API filter conversion.
 *
 * Features:
 * - Initialize filters from URL params or defaults
 * - Sync filter changes to URL
 * - Convert filter state to API-compatible params (apiFilters)
 *
 * NOTE: Client-side filtering has been removed. All filtering
 * is now done server-side via the getOrderQueue API.
 * Filter options (colors, sizes, etc.) come from the
 * separate getFilterOptions endpoint via useFilterOptions hook.
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { OrdersPageFilters, QueueStatusFilter, OrderTypeFilter, AgeRangeFilter, FulfillmentTypeFilter, SortOrderFilter } from '../components/OrdersFilterBar';
import { DEFAULT_FILTERS } from '../components/OrdersFilterBar';
import type { OrderQueueApiFilters } from './useOrderQueue';
import { ALL_CLIENTS } from '@/features/inventory/hooks/useClientOrgFilter';
import { QUEUE_VIEW_PARAM, WAITING_SEARCH_PARAM } from '../utils/waiting-on-blank';

/**
 * Params the page owns apart from the filters (BLU-1506: the Waiting on blank
 * view and its own search). The filter rebuild below starts from scratch, so
 * it carries these over from the current URL rather than dropping them.
 */
const NON_FILTER_PARAMS = [QUEUE_VIEW_PARAM, WAITING_SEARCH_PARAM] as const;

/** Updater function pattern for setFilters */
type FiltersUpdater = OrdersPageFilters | ((prev: OrdersPageFilters) => OrdersPageFilters);

interface UseOrderFiltersReturn {
  filters: OrdersPageFilters;
  /** Set filters directly or via updater function to avoid stale closures */
  setFilters: (updater: FiltersUpdater) => void;
  /** API-compatible filters (omits defaults/empty values) */
  apiFilters: OrderQueueApiFilters | undefined;
  /**
   * BLU-787: `apiFilters` minus `search`, for `useBatchScopes`.
   *
   * ⚠ `search` must never reach the resolver, and not just because
   * `resolveBatchScopesSchema` structurally omits it server-side (it does,
   * silently — no error). The tRPC query key is built from the INPUT VALUE,
   * so if `search` were included here, every keystroke would mint a new
   * cache key and re-fire the resolver's full unpaginated ready-tab sweep
   * (~1,237 unbatched orders at Threadheads, over the HK proxy at Ningbo).
   * Stripping it here — before it ever reaches `useBatchScopes` — is what
   * keeps that query key stable while the operator types.
   */
  scopeFilters: Omit<OrderQueueApiFilters, 'search'> | undefined;
  clearFilters: () => void;
}

// Search input sanitization
const MAX_SEARCH_LENGTH = 200;

/**
 * Sanitize search input: trim, limit length, remove control characters
 */
function sanitizeSearchInput(input: string): string {
  // Remove control characters (ASCII 0-31) while keeping printable characters
  // eslint-disable-next-line no-control-regex
  const controlCharRegex = /[\x00-\x1F]/g;
  return input
    .trim()
    .slice(0, MAX_SEARCH_LENGTH)
    .replace(controlCharRegex, '');
}

/**
 * BLU-1423: `?client=` carries the person's own client pick: an org id, or
 * `all` for an explicit All clients. Absent = the floor's default. Anything
 * else is ignored.
 */
const CLIENT_PARAM = 'client';
const CLIENT_PARAM_ALL = 'all';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** `?client=` → an org id or `ALL_CLIENTS`; undefined when absent or malformed. */
export function readClientParam(params: URLSearchParams): string | undefined {
  const raw = params.get(CLIENT_PARAM);
  if (raw === CLIENT_PARAM_ALL) return ALL_CLIENTS;
  return raw && UUID_RE.test(raw) ? raw : undefined;
}

function writeClientParam(params: URLSearchParams, clientChoice: string | undefined): void {
  if (clientChoice === undefined) {
    params.delete(CLIENT_PARAM);
  } else {
    params.set(CLIENT_PARAM, clientChoice === ALL_CLIENTS ? CLIENT_PARAM_ALL : clientChoice);
  }
}

/**
 * The URL's client pick as the page opened, read once. Separate from
 * `useOrderFilters` because the page's client filter needs it before the
 * filters hook can run (that hook takes the filter's result).
 */
export function useInitialClientChoice(): string | undefined {
  const [searchParams] = useSearchParams();
  const [choice] = useState(() => readClientParam(searchParams));
  return choice;
}

export function useOrderFilters({
  persistToUrl = true,
  /**
   * BLU-367/BLU-787: the operator's per-client selection (`useClientOrgFilter`
   * on the page). NOT part of `OrdersPageFilters` (the URL carries only the
   * person's own pick, `clientChoice` below), but it still needs to land in
   * `apiFilters` so the queue query and the batch-scope resolver both narrow
   * to it.
   */
  clientOrganizationId,
  /**
   * BLU-1423: the person's own client pick (`useClientOrgFilter`'s
   * `pickedOrgFilter`), kept in the URL as `?client=` so a refresh or the
   * back button keeps it. Undefined = none (the floor's default applies).
   */
  clientChoice,
}: { persistToUrl?: boolean; clientOrganizationId?: string; clientChoice?: string } = {}): UseOrderFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  // Read by the rebuild below, which runs inside a state updater.
  const clientChoiceRef = useRef(clientChoice);
  clientChoiceRef.current = clientChoice;

  // Initialize filters from URL or defaults
  const [filters, setFiltersState] = useState<OrdersPageFilters>(() => {
    if (!persistToUrl) return DEFAULT_FILTERS;

    // Queue status (single value now)
    // Phase 12.6: 'oos' removed from valid values - default to 'ready' if 'oos' in URL
    const statusParam = searchParams.get('status');
    const queueStatus: QueueStatusFilter =
      statusParam === 'all' || statusParam === 'ready'
        ? statusParam
        : DEFAULT_FILTERS.queueStatus;

    // Order type (single value)
    const orderTypeParam = searchParams.get('orderType');
    const orderType: OrderTypeFilter =
      orderTypeParam === 'single' || orderTypeParam === 'multi' || orderTypeParam === 'all'
        ? orderTypeParam
        : DEFAULT_FILTERS.orderType;

    // Age range (single value)
    const ageParam = searchParams.get('age');
    const ageRange: AgeRangeFilter =
      ageParam === 'today' || ageParam === '1-3days' || ageParam === '3+days' || ageParam === 'all'
        ? ageParam
        : DEFAULT_FILTERS.ageRange;

    // Fulfillment type (single value)
    const fulfillmentParam = searchParams.get('fulfillment');
    const fulfillmentType: FulfillmentTypeFilter =
      fulfillmentParam === 'pod_only' || fulfillmentParam === 'pap_only' || fulfillmentParam === 'mixed' || fulfillmentParam === 'all' || fulfillmentParam === 'pod_dtg' || fulfillmentParam === 'pod_dtf'
        ? fulfillmentParam
        : DEFAULT_FILTERS.fulfillmentType;

    // Sort order (single value)
    const sortParam = searchParams.get('sort');
    const sortOrder: SortOrderFilter =
      sortParam === 'oldest' || sortParam === 'newest'
        ? sortParam
        : DEFAULT_FILTERS.sortOrder;

    // Search query (sanitized)
    const searchQueryParam = searchParams.get('search') ?? '';
    const searchQuery = sanitizeSearchInput(searchQueryParam);

    const colors = searchParams.get('colors')?.split(',').filter(Boolean) ?? [];
    const sizes = searchParams.get('sizes')?.split(',').filter(Boolean) ?? [];
    const productTypes = searchParams.get('types')?.split(',').filter(Boolean) ?? [];
    const tags = searchParams.get('tags')?.split(',').filter(Boolean) ?? [];

    // Match mode from URL (default 'any')
    const colorMatchParam = searchParams.get('colorMatch');
    const colorMatchMode: 'any' | 'only' =
      colorMatchParam === 'only' ? 'only' : 'any';
    const sizeMatchParam = searchParams.get('sizeMatch');
    const sizeMatchMode: 'any' | 'only' =
      sizeMatchParam === 'only' ? 'only' : 'any';

    return {
      queueStatus,
      orderType,
      ageRange,
      fulfillmentType,
      sortOrder,
      searchQuery,
      colors,
      colorMatchMode,
      sizes,
      sizeMatchMode,
      productTypes,
      tags,
    };
  });

  // Sync filters to URL - supports both direct value and updater function
  const setFilters = useCallback((updater: FiltersUpdater) => {
    setFiltersState(prevFilters => {
      // Resolve the new filters (handles both direct value and updater fn)
      const newFilters = typeof updater === 'function' ? updater(prevFilters) : updater;

      // Skip if no change (optimization for updater functions that return prev)
      if (newFilters === prevFilters) return prevFilters;

      if (persistToUrl) {
        const params = new URLSearchParams();

        // Only add non-default values to URL
        if (newFilters.queueStatus !== DEFAULT_FILTERS.queueStatus) {
          params.set('status', newFilters.queueStatus);
        }
        if (newFilters.orderType !== DEFAULT_FILTERS.orderType) {
          params.set('orderType', newFilters.orderType);
        }
        if (newFilters.ageRange !== DEFAULT_FILTERS.ageRange) {
          params.set('age', newFilters.ageRange);
        }
        if (newFilters.fulfillmentType !== DEFAULT_FILTERS.fulfillmentType) {
          params.set('fulfillment', newFilters.fulfillmentType);
        }
        if (newFilters.sortOrder !== DEFAULT_FILTERS.sortOrder) {
          params.set('sort', newFilters.sortOrder);
        }
        // Search is ephemeral but include in URL for shareability
        if (newFilters.searchQuery) {
          params.set('search', newFilters.searchQuery);
        }
        if (newFilters.colors.length > 0) {
          params.set('colors', newFilters.colors.join(','));
        }
        if (newFilters.colorMatchMode !== 'any') {
          params.set('colorMatch', newFilters.colorMatchMode);
        }
        if (newFilters.sizes.length > 0) {
          params.set('sizes', newFilters.sizes.join(','));
        }
        if (newFilters.sizeMatchMode !== 'any') {
          params.set('sizeMatch', newFilters.sizeMatchMode);
        }
        if (newFilters.productTypes.length > 0) {
          params.set('types', newFilters.productTypes.join(','));
        }
        if (newFilters.tags.length > 0) {
          params.set('tags', newFilters.tags.join(','));
        }
        // The rebuild starts from scratch, so the client pick goes in here too.
        writeClientParam(params, clientChoiceRef.current);

        setSearchParams(
          (prev) => {
            for (const key of NON_FILTER_PARAMS) {
              const value = prev.get(key);
              if (value) params.set(key, value);
            }
            return params;
          },
          { replace: true },
        );
      }

      return newFilters;
    });
  }, [persistToUrl, setSearchParams]);

  // A client pick changes outside setFilters: write just that key. Skips the
  // mount, where the URL already says what the page opened on.
  const writtenClientChoiceRef = useRef(clientChoice);
  useEffect(() => {
    if (!persistToUrl || writtenClientChoiceRef.current === clientChoice) return;
    writtenClientChoiceRef.current = clientChoice;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      writeClientParam(next, clientChoice);
      return next;
    }, { replace: true });
  }, [persistToUrl, clientChoice, setSearchParams]);

  // Clear filters
  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, [setFilters]);

  // Convert UI filter state to API filter params (omitting defaults/empty values)
  const apiFilters = useMemo((): OrderQueueApiFilters | undefined => {
    const f: OrderQueueApiFilters = {};
    let hasAny = false;

    if (filters.searchQuery) {
      f.search = sanitizeSearchInput(filters.searchQuery);
      hasAny = true;
    }
    if (filters.ageRange !== 'all') {
      f.ageRange = filters.ageRange;
      hasAny = true;
    }
    if (filters.orderType !== 'all') {
      f.orderType = filters.orderType;
      hasAny = true;
    }
    if (filters.fulfillmentType !== 'all') {
      f.fulfillmentType = filters.fulfillmentType;
      hasAny = true;
    }
    if (filters.tags.length > 0) {
      f.tags = filters.tags;
      hasAny = true;
    }
    if (filters.colors.length > 0) {
      f.colors = filters.colors;
      if (filters.colorMatchMode === 'only') f.colorsExclusive = true;
      hasAny = true;
    }
    if (filters.sizes.length > 0) {
      f.sizes = filters.sizes;
      if (filters.sizeMatchMode === 'only') f.sizesExclusive = true;
      hasAny = true;
    }
    if (filters.productTypes.length > 0) {
      f.productTypes = filters.productTypes;
      hasAny = true;
    }
    if (clientOrganizationId) {
      f.clientOrganizationId = clientOrganizationId;
      hasAny = true;
    }

    return hasAny ? f : undefined;
  }, [filters, clientOrganizationId]);

  // BLU-787: see the docblock on `scopeFilters` above — `search` is stripped
  // before this ever reaches `useBatchScopes`.
  const scopeFilters = useMemo((): Omit<OrderQueueApiFilters, 'search'> | undefined => {
    if (!apiFilters) return undefined;
    const { search: _search, ...rest } = apiFilters;
    return Object.keys(rest).length > 0 ? rest : undefined;
  }, [apiFilters]);

  return {
    filters,
    setFilters,
    apiFilters,
    scopeFilters,
    clearFilters,
  };
}
